// 사용자 관리 화면 (관리자 전용)
import React, { useState, useEffect, useCallback, useRef } from 'react';
import PageLayout from '../../components/PageLayout';
import {
  getUsers, createUser, approveUser, deactivateUser, resetPassword,
  getUserPermissions, saveUserPermissions,
  getUserDetail, getLoginHistory, getAgreeHistory, updateUser,
} from '../../api/users';
import './UserManagePage.css';

// ─────────────────────── helpers ──────────────────────────
const fmtDt = (dt) => {
  if (!dt) return '-';
  const d = new Date(dt);
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')} ${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`;
};

function getStatus(u) {
  if (u.is_active) return 'active';
  if (!u.approve_date) return 'pending';
  return 'inactive';
}

const STATUS = {
  active:   { label: '활성',    cls: 'um-status--active'   },
  pending:  { label: '승인대기', cls: 'um-status--pending'  },
  inactive: { label: '비활성',  cls: 'um-status--inactive' },
};

const agreeIcon = (v) => {
  if (v === null || v === undefined) return '—';
  return v ? '✅' : '❌';
};

// ─────────────────────── UserAddModal ─────────────────────
const UserAddModal = ({ onSave, onClose }) => {
  const [form, setForm] = useState({ user_id: '', user_name: '', email: '', password: '', is_active: true, is_admin: false });
  const [error, setError] = useState('');
  const [busy,  setBusy]  = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const submit = async () => {
    setError('');
    if (!form.user_id.trim())   { setError('아이디를 입력하세요.'); return; }
    if (!form.user_name.trim()) { setError('이름을 입력하세요.'); return; }
    if (!form.password)         { setError('비밀번호를 입력하세요.'); return; }
    setBusy(true);
    try {
      await onSave({ ...form, user_id: form.user_id.trim(), user_name: form.user_name.trim(), email: form.email.trim() || undefined });
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || '등록 실패');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="um-overlay">
      <div className="um-modal">
        <div className="um-modal-hd">사용자 추가 <button className="um-modal-close" onClick={onClose}>✕</button></div>
        <div className="um-modal-bd">
          {[['아이디 *','user_id','text'],['이름 *','user_name','text'],['이메일','email','email'],['비밀번호 *','password','password']].map(([lbl,k,t]) => (
            <div className="um-form-row" key={k}>
              <label>{lbl}</label>
              <input type={t} value={form[k]} onChange={e => set(k, e.target.value)} autoComplete="off" />
            </div>
          ))}
          <div className="um-form-checks">
            <label><input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} /> 즉시 활성화</label>
            <label><input type="checkbox" checked={form.is_admin}  onChange={e => set('is_admin',  e.target.checked)} /> 관리자</label>
          </div>
          {error && <p className="um-form-error">{error}</p>}
          <div className="um-modal-actions">
            <button className="btn btn-sm btn-outline" onClick={onClose}>취소</button>
            <button className="btn btn-sm btn-primary" onClick={submit} disabled={busy}>{busy ? '등록 중...' : '등록'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────── RejectPopup ──────────────────────
const RejectPopup = ({ userId, onConfirm, onClose }) => {
  const [reason, setReason] = useState('');
  const [busy,   setBusy]   = useState(false);

  const submit = async () => {
    setBusy(true);
    try { await onConfirm(userId, reason.trim()); onClose(); }
    catch { /* 에러는 부모에서 처리 */ }
    finally { setBusy(false); }
  };

  return (
    <div className="um-overlay">
      <div className="um-modal um-modal--sm">
        <div className="um-modal-hd">거절 사유 입력 <button className="um-modal-close" onClick={onClose}>✕</button></div>
        <div className="um-modal-bd">
          <p className="um-reject-hint">거절 사유를 입력하세요. (선택)</p>
          <textarea className="um-reject-ta" value={reason} onChange={e => setReason(e.target.value)} rows={4} autoFocus placeholder="거절 사유..." />
          <div className="um-modal-actions">
            <button className="btn btn-sm btn-outline" onClick={onClose}>취소</button>
            <button className="btn btn-sm btn-danger" onClick={submit} disabled={busy}>{busy ? '처리 중...' : '거절'}</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ─────────────────────── DetailPopup ──────────────────────
const DetailPopup = ({ userId, onClose, onMemoSaved }) => {
  const [tab,          setTab]          = useState('info');
  const [detailData,   setDetailData]   = useState(null);
  const [agreeHist,    setAgreeHist]    = useState(null);
  const [loginHist,    setLoginHist]    = useState(null);
  const [loginPage,    setLoginPage]    = useState(1);
  const [loginTotal,   setLoginTotal]   = useState(0);
  const [loginLoading, setLoginLoading] = useState(false);
  const [memo,         setMemo]         = useState('');
  const [memoSaving,   setMemoSaving]   = useState(false);
  const [loading,      setLoading]      = useState(true);
  const [errMsg,       setErrMsg]       = useState('');

  // 기본정보 로딩
  useEffect(() => {
    getUserDetail(userId)
      .then(res => { setDetailData(res); setMemo(res.user?.memo || ''); })
      .catch(() => setErrMsg('상세 정보 로딩 실패'))
      .finally(() => setLoading(false));
  }, [userId]);

  // 동의 이력 (탭 전환 시 최초 1회)
  useEffect(() => {
    if (tab !== 'agree' || agreeHist !== null) return;
    getAgreeHistory(userId)
      .then(res => setAgreeHist(res.items || []))
      .catch(() => setAgreeHist([]));
  }, [tab, agreeHist, userId]);

  // 접근 이력 (탭/페이지 변경 시)
  useEffect(() => {
    if (tab !== 'login') return;
    setLoginLoading(true);
    getLoginHistory(userId, loginPage, 20)
      .then(res => { setLoginHist(res.items || []); setLoginTotal(res.total || 0); })
      .catch(() => setLoginHist([]))
      .finally(() => setLoginLoading(false));
  }, [tab, userId, loginPage]);

  const saveMemo = async () => {
    setMemoSaving(true);
    try { await updateUser(userId, { memo }); onMemoSaved?.(); }
    finally { setMemoSaving(false); }
  };

  const u  = detailData?.user;
  const ag = detailData?.agree_summary;
  const totalPages = Math.ceil(loginTotal / 20);

  return (
    <div className="um-overlay">
      <div className="um-detail-modal">
        <div className="um-modal-hd">
          {u ? `${u.user_name} (${u.user_id})` : userId} 상세 정보
          <button className="um-modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="um-detail-tabs">
          {[['info','기본정보'],['agree','동의이력'],['login','접근이력']].map(([t,lbl]) => (
            <button key={t} className={`um-detail-tab ${tab === t ? 'active' : ''}`} onClick={() => setTab(t)}>{lbl}</button>
          ))}
        </div>
        <div className="um-detail-body">

          {/* ── 기본정보 탭 ── */}
          {tab === 'info' && (
            loading ? <div className="um-loading">{errMsg || '로딩 중...'}</div> :
            !u ? <div className="um-loading">사용자 정보를 찾을 수 없습니다.</div> : (
              <div className="um-info-tab">
                <table className="um-info-table">
                  <tbody>
                    {[
                      ['아이디', u.user_id],
                      ['이름',   u.user_name],
                      ['이메일', u.email || '-'],
                      ['연락처', u.phone || '-'],
                      ['가입일', fmtDt(u.created_at)],
                      ['마지막 로그인', fmtDt(u.last_login)],
                      ['로그인 횟수', `${u.login_count}회`],
                      ['승인일시', fmtDt(u.approve_date)],
                      ['승인 관리자', u.approve_by || '-'],
                      ['거절사유', u.reject_reason || '-'],
                    ].map(([lbl, val]) => (
                      <tr key={lbl}><th>{lbl}</th><td>{val}</td></tr>
                    ))}
                    <tr>
                      <th>동의현황</th>
                      <td className="um-agree-icons">
                        <span>{agreeIcon(ag?.terms?.is_agreed)} 약관</span>
                        <span>{agreeIcon(ag?.privacy?.is_agreed)} 개인정보</span>
                        <span>{agreeIcon(ag?.marketing?.is_agreed)} 마케팅</span>
                      </td>
                    </tr>
                    <tr>
                      <th>관리자 메모</th>
                      <td>
                        <textarea className="um-memo-ta" value={memo} onChange={e => setMemo(e.target.value)} rows={3} placeholder="메모 입력..." />
                        <button className="btn btn-sm btn-outline um-memo-btn" onClick={saveMemo} disabled={memoSaving}>
                          {memoSaving ? '저장 중...' : '메모 저장'}
                        </button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )
          )}

          {/* ── 동의이력 탭 ── */}
          {tab === 'agree' && (
            <div className="um-agree-tab">
              {ag && (
                <div className="um-agree-summary">
                  {[['이용약관', ag.terms], ['개인정보', ag.privacy], ['마케팅', ag.marketing]].map(([lbl, item]) => (
                    <div key={lbl} className={`um-agree-sum-item ${item?.is_agreed ? 'agreed' : ''}`}>
                      <div className="um-agree-sum-type">{lbl}</div>
                      <div className="um-agree-sum-icon">{agreeIcon(item?.is_agreed)}</div>
                      <div className="um-agree-sum-ver">{item?.version || '-'}</div>
                      <div className="um-agree-sum-dt">{fmtDt(item?.dt)}</div>
                    </div>
                  ))}
                </div>
              )}
              <table className="um-hist-table">
                <thead>
                  <tr><th>동의유형</th><th>버전</th><th>동의여부</th><th>일시</th><th>IP</th></tr>
                </thead>
                <tbody>
                  {agreeHist === null ? (
                    <tr><td colSpan={5} className="um-td-empty">로딩 중...</td></tr>
                  ) : agreeHist.length === 0 ? (
                    <tr><td colSpan={5} className="um-td-empty">동의 이력이 없습니다.</td></tr>
                  ) : agreeHist.map((r, i) => (
                    <tr key={i}>
                      <td>{r.agree_type}</td>
                      <td>{r.agree_version}</td>
                      <td className={r.is_agreed ? 'um-cell-agreed' : 'um-cell-disagreed'}>{r.is_agreed ? '동의' : '철회'}</td>
                      <td>{fmtDt(r.agree_dt)}</td>
                      <td>{r.ip_address || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* ── 접근이력 탭 ── */}
          {tab === 'login' && (
            <div className="um-login-tab">
              {loginLoading ? <div className="um-loading">로딩 중...</div> : (
                <>
                  <table className="um-hist-table">
                    <thead>
                      <tr><th>로그인일시</th><th>로그아웃</th><th>IP</th><th>기기</th><th>결과</th></tr>
                    </thead>
                    <tbody>
                      {!loginHist || loginHist.length === 0 ? (
                        <tr><td colSpan={5} className="um-td-empty">접근 이력이 없습니다.</td></tr>
                      ) : loginHist.map((r, i) => (
                        <tr key={i} className={r.login_result !== 'SUCCESS' ? 'um-row-fail' : ''}>
                          <td>{fmtDt(r.login_dt)}</td>
                          <td>{fmtDt(r.logout_dt)}</td>
                          <td>{r.ip_address || '-'}</td>
                          <td className="um-td-device">{r.device_info || '-'}</td>
                          <td>{r.login_result}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {totalPages > 1 && (
                    <div className="um-paging">
                      <button onClick={() => setLoginPage(p => p - 1)} disabled={loginPage === 1}>◀</button>
                      <span>{loginPage} / {totalPages}</span>
                      <button onClick={() => setLoginPage(p => p + 1)} disabled={loginPage >= totalPages}>▶</button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ─────────────────────── Main ─────────────────────────────
const UserManagePage = ({ isActive }) => {
  const [users,     setUsers]     = useState([]);
  const [perms,     setPerms]     = useState([]);
  const [filterTab, setFilterTab] = useState('all');
  const [search,    setSearch]    = useState('');
  const [selected,  setSelected]  = useState(null);
  const [busyUser,  setBusyUser]  = useState('');
  const [busySave,  setBusySave]  = useState(false);
  const [message,   setMessage]   = useState({ text: '', type: '' });

  const [addOpen,    setAddOpen]    = useState(false);
  const [rejectPop,  setRejectPop]  = useState({ open: false, userId: '' });
  const [detailUid,  setDetailUid]  = useState(null);

  const [menuNameWidth, setMenuNameWidth] = useState(120);

  const lastClickRef = useRef({ time: 0, userId: '' });
  const readAllRef   = useRef(null);
  const writeAllRef  = useRef(null);

  const onResizerDown = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = menuNameWidth;
    const onMove = (ev) => setMenuNameWidth(Math.max(80, startW + ev.clientX - startX));
    const onUp   = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup',   onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup',   onUp);
  };

  const showMsg = useCallback((text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 4000);
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      const res  = await getUsers();
      const list = res.data || [];
      setUsers(list);
      setSelected(prev => {
        if (!prev) return null;
        return list.find(u => u.user_id === prev.user_id) || null;
      });
    } catch (e) {
      showMsg('사용자 목록 로딩 실패', 'error');
    }
  }, [showMsg]);

  useEffect(() => {
    if (!isActive) return;
    loadUsers();
  }, [isActive, loadUsers]);

  // ── 필터 ──
  const filteredUsers = users.filter(u => {
    const s = getStatus(u);
    if (filterTab !== 'all' && s !== filterTab) return false;
    if (search) {
      const q = search.toLowerCase();
      if (!u.user_id.toLowerCase().includes(q) && !u.user_name.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const tabCounts = {
    all:      users.length,
    active:   users.filter(u => getStatus(u) === 'active').length,
    pending:  users.filter(u => getStatus(u) === 'pending').length,
    inactive: users.filter(u => getStatus(u) === 'inactive').length,
  };

  // ── 사용자 선택 ──
  const selectUser = async (u) => {
    setSelected(u);
    try {
      const res = await getUserPermissions(u.user_id);
      setPerms(res.permissions || []);
    } catch {
      showMsg('권한 조회 실패', 'error');
      setPerms([]);
    }
  };

  // ── 카드 클릭 (더블클릭 → 상세팝업) ──
  const handleCardClick = (u) => {
    const now  = Date.now();
    const last = lastClickRef.current;
    if (now - last.time < 350 && last.userId === u.user_id) {
      lastClickRef.current = { time: 0, userId: '' };
      setDetailUid(u.user_id);
    } else {
      lastClickRef.current = { time: now, userId: u.user_id };
      selectUser(u);
    }
  };

  // ── 권한 토글 ──
  const togglePerm = (menuCode, field) => {
    setPerms(prev => prev.map(p => {
      if (p.menu_code !== menuCode) return p;
      const next = { ...p, [field]: !p[field] };
      if (field === 'can_read'  && !next.can_read)  next.can_write = false;
      if (field === 'can_write' && next.can_write)   next.can_read  = true;
      return next;
    }));
  };

  // ── 전체 체크박스 상태 ──
  const hasP    = perms.length > 0;
  const allRead  = hasP && perms.every(p => p.can_read);
  const someRead = hasP && !allRead && perms.some(p => p.can_read);
  const allWrite  = hasP && perms.every(p => p.can_write);
  const someWrite = hasP && !allWrite && perms.some(p => p.can_write);

  useEffect(() => {
    if (readAllRef.current)  readAllRef.current.indeterminate  = someRead;
    if (writeAllRef.current) writeAllRef.current.indeterminate = someWrite;
  }, [someRead, someWrite]);

  const handleAllRead = (e) => {
    const c = e.target.checked;
    setPerms(prev => prev.map(p => ({ ...p, can_read: c, can_write: c ? p.can_write : false })));
  };
  const handleAllWrite = (e) => {
    const c = e.target.checked;
    setPerms(prev => prev.map(p => ({ ...p, can_write: c, can_read: c ? true : p.can_read })));
  };

  // ── 권한 저장 ──
  const handleSavePerms = async () => {
    if (!selected) return;
    setBusySave(true);
    try {
      await saveUserPermissions(selected.user_id, perms);
      showMsg('권한이 저장되었습니다.');
    } catch (e) {
      showMsg('저장 실패: ' + e.message, 'error');
    } finally {
      setBusySave(false);
    }
  };

  // ── 사용자 액션 ──
  const handleAction = async (action, userId) => {
    setBusyUser(userId);
    try {
      let res;
      if (action === 'approve')    res = await approveUser(userId);
      if (action === 'deactivate') res = await deactivateUser(userId);
      if (action === 'resetpw')    res = await resetPassword(userId);
      showMsg(res?.message || '처리되었습니다.');
      await loadUsers();
    } catch (e) {
      showMsg(e.response?.data?.detail || e.message, 'error');
    } finally {
      setBusyUser('');
    }
  };

  // ── 거절 ──
  const handleReject = async (userId, reason) => {
    setBusyUser(userId);
    try {
      await updateUser(userId, { reject_reason: reason || '거절됨' });
      showMsg('거절 처리되었습니다.');
      await loadUsers();
    } catch (e) {
      showMsg(e.response?.data?.detail || e.message, 'error');
    } finally {
      setBusyUser('');
    }
  };

  // ── 권한 테이블 구성 ──
  const groupCounts = {};
  perms.forEach(p => { groupCounts[p.group_code] = (groupCounts[p.group_code] || 0) + 1; });
  const enrichedPerms = perms.map((p, i) => ({
    ...p,
    isGroupStart: i === 0 || p.group_code !== perms[i - 1].group_code,
  }));

  const selStatus = selected ? getStatus(selected) : null;
  const isBusy    = !!busyUser;

  return (
    <PageLayout title="사용자 관리" message={message}>
      <div className="um-layout">

        {/* ── 좌측 패널 ── */}
        <div className="um-left">
          {/* 헤더 */}
          <div className="um-panel-hd">
            <span>사용자 <span className="um-count">{filteredUsers.length} / {users.length}명</span></span>
            <button className="btn btn-sm btn-primary" onClick={() => setAddOpen(true)}>+ 추가</button>
          </div>

          {/* 검색 */}
          <div className="um-search-wrap">
            <input className="um-search" type="text" value={search}
              onChange={e => setSearch(e.target.value)} placeholder="아이디/이름 검색..." />
          </div>

          {/* 필터 탭 */}
          <div className="um-filter-tabs">
            {[['all','전체'],['active','활성'],['pending','승인대기'],['inactive','비활성']].map(([t, lbl]) => (
              <button key={t} className={`um-filter-tab ${filterTab === t ? 'active' : ''}`}
                onClick={() => setFilterTab(t)}>
                {lbl} <span className="um-tab-cnt">{tabCounts[t]}</span>
              </button>
            ))}
          </div>

          {/* 사용자 카드 목록 */}
          <div className="um-user-list">
            {filteredUsers.length === 0 && <div className="um-empty">해당 사용자가 없습니다.</div>}
            {filteredUsers.map(u => {
              const st = getStatus(u);
              const isSelected = selected?.user_id === u.user_id;
              return (
                <div key={u.user_id}
                  className={`um-user-card${isSelected ? ' selected' : ''}`}
                  onClick={() => handleCardClick(u)}
                  title="더블클릭: 상세 정보">
                  <div className="um-card-row1">
                    <span className="um-card-name">{u.user_name}</span>
                    {u.is_admin && <span className="um-badge-admin">관리자</span>}
                    {u.is_temp_password && <span className="um-badge-tmp">임시PW</span>}
                    {u.reject_reason && <span className="um-badge-rejected">거절</span>}
                    <span className={`um-status-badge ${STATUS[st].cls}`}>{STATUS[st].label}</span>
                  </div>
                  <div className="um-card-id">{u.user_id}</div>
                  <div className="um-card-login">마지막 로그인: {fmtDt(u.last_login)}</div>
                  <div className="um-card-agree">
                    <span>{agreeIcon(u.agree_terms)} 약관</span>
                    <span>{agreeIcon(u.agree_privacy)} 개인정보</span>
                    <span>{agreeIcon(u.agree_marketing)} 마케팅</span>
                  </div>
                </div>
              );
            })}
          </div>

          {/* 액션 버튼 */}
          {selected && (
            <div className="um-actions">
              <div className="um-actions-title">[{selected.user_name}] 관리</div>
              <div className="um-actions-btns">
                {selStatus === 'pending' && <>
                  <button className="btn btn-sm btn-primary"  disabled={isBusy} onClick={() => handleAction('approve', selected.user_id)}>승인</button>
                  <button className="btn btn-sm btn-danger"   disabled={isBusy} onClick={() => setRejectPop({ open: true, userId: selected.user_id })}>거절</button>
                </>}
                {selStatus === 'active' && (
                  <button className="btn btn-sm btn-outline"  disabled={isBusy} onClick={() => handleAction('deactivate', selected.user_id)}>비활성화</button>
                )}
                {selStatus === 'inactive' && (
                  <button className="btn btn-sm btn-primary"  disabled={isBusy} onClick={() => handleAction('approve', selected.user_id)}>활성화</button>
                )}
                <button className="btn btn-sm btn-outline" disabled={isBusy} onClick={() => handleAction('resetpw', selected.user_id)}>비밀번호 초기화</button>
              </div>
            </div>
          )}
        </div>

        {/* ── 우측 패널: 메뉴 권한 ── */}
        <div className="um-right">
          {!selected ? (
            <div className="um-no-select">좌측에서 사용자를 선택하세요.</div>
          ) : (
            <>
              <div className="um-panel-hd um-perm-hd">
                <div className="um-perm-hd-left">
                  <span className="um-perm-title">{selected.user_name} ({selected.user_id}) 메뉴 권한</span>
                  {!selected.is_admin && (
                    <span className="um-allchecks">
                      <label className="um-allcheck-lbl">
                        <input type="checkbox" ref={readAllRef} checked={allRead} onChange={handleAllRead} />
                        조회 전체
                      </label>
                      <label className="um-allcheck-lbl">
                        <input type="checkbox" ref={writeAllRef} checked={allWrite} onChange={handleAllWrite} />
                        등록 전체
                      </label>
                    </span>
                  )}
                </div>
                <button className="btn btn-sm btn-primary" style={{ marginLeft: '28px' }} onClick={handleSavePerms} disabled={busySave || selected.is_admin}>
                  {busySave ? '저장 중...' : '저장'}
                </button>
              </div>

              {selected.is_admin ? (
                <div className="um-admin-notice">관리자 계정은 모든 메뉴에 자동으로 접근 가능합니다.</div>
              ) : (
                <div className="um-perm-body">
                  <table className="um-perm-table">
                    <thead>
                      <tr>
                        <th className="um-th-group">메뉴그룹</th>
                        <th className="um-th-menuname" style={{ width: menuNameWidth, minWidth: 80 }}>
                          화면명<span className="um-col-resizer" onMouseDown={onResizerDown} />
                        </th>
                        <th className="um-th-check">조회</th>
                        <th className="um-th-write">등록/수정</th>
                        <th className="um-th-note">비고</th>
                      </tr>
                    </thead>
                    <tbody>
                      {enrichedPerms.map(p => (
                        <tr key={p.menu_code}
                          className={`${p.isGroupStart ? 'um-group-start' : ''} ${p.is_admin ? 'um-row-admin' : ''}`}>
                          {p.isGroupStart && (
                            <td rowSpan={groupCounts[p.group_code]} className="um-td-group">{p.group_name}</td>
                          )}
                          <td>{p.menu_name}</td>
                          <td className="um-td-check">
                            <input type="checkbox" checked={p.can_read} onChange={() => togglePerm(p.menu_code, 'can_read')} />
                          </td>
                          <td className="um-td-check">
                            <input type="checkbox" checked={p.can_write} onChange={() => togglePerm(p.menu_code, 'can_write')} />
                          </td>
                          <td className="um-td-note">{p.is_admin ? '🔒 관리자' : ''}</td>
                        </tr>
                      ))}
                      {perms.length === 0 && (
                        <tr><td colSpan={5} className="um-td-empty">권한 설정 가능한 메뉴가 없습니다.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── 팝업들 ── */}
      {addOpen && (
        <UserAddModal
          onSave={async (body) => { await createUser(body); showMsg('사용자가 등록되었습니다.'); await loadUsers(); }}
          onClose={() => setAddOpen(false)}
        />
      )}
      {rejectPop.open && (
        <RejectPopup
          userId={rejectPop.userId}
          onConfirm={handleReject}
          onClose={() => setRejectPop({ open: false, userId: '' })}
        />
      )}
      {detailUid && (
        <DetailPopup
          userId={detailUid}
          onClose={() => setDetailUid(null)}
          onMemoSaved={loadUsers}
        />
      )}
    </PageLayout>
  );
};

export default UserManagePage;
